/**
 * Menschlich lesbare Bezeichnungen des Prüfungstyps für Berichts-Exporte (Excel, PDF) – an einer
 * Stelle gepflegt, damit beide Export-Formate denselben Wortlaut zeigen.
 */

export function reportTypeLabel(checkType: string | null | undefined): string {
  const type = (checkType ?? '').toUpperCase();
  if (type === 'IDS') return 'IDS-Bericht';
  if (type === 'HYBRID') return 'Hybride Prüfung';
  if (type === 'KI' || type === 'AI' || type === 'HOAI') return 'KI-Prüfung';
  return 'Prüfbericht';
}

/**
 * Bereinigt einen Namensteil für Dateinamen: HTML-Tags/Entities, unter Windows/macOS verbotene
 * Zeichen (`\ / : * ? " < > |`) und Steuerzeichen werden entfernt bzw. ersetzt.
 */
export function sanitizeFileNamePart(name: string, maxLength = 150): string {
  return name
    .replace(/<\/?[a-z][^<>]*>/gi, '')
    .replace(/&(amp|lt|gt|quot|#39|apos|nbsp);/g, (_, entity: string) =>
      ({amp: '&', lt: '', gt: '', quot: '', '#39': '\'', apos: '\'', nbsp: ' '} as Record<string, string>)[entity])
    .replace(/[\\/?*[\]:"<>|]/g, '-')
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/-(\s*-)+/g, '-')
    .trim()
    .replace(/^[.\s-]+|[.\s-]+$/g, '')
    .slice(0, maxLength)
    .trim();
}

function fileNameDate(date: Date | null | undefined): string {
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Dateiname eines Berichts-Exports: `<Typ> - <Prüflauf-Name> - <JJJJ-MM-TT>.<ext>`. Der
 * Prüflauf-Name unterscheidet mehrere Läufe desselben myChecks; fehlt er, greift der Regelset-Name.
 */
export function reportExportFileName(
  checkType: string | null | undefined,
  runName: string | null | undefined,
  fallbackName: string | null | undefined,
  checkDate: Date | null | undefined,
  extension: string,
): string {
  const name = sanitizeFileNamePart(runName ?? '') || sanitizeFileNamePart(fallbackName ?? '') || 'Bericht';
  const parts = [reportTypeLabel(checkType), name, fileNameDate(checkDate)].filter(Boolean);
  return `${sanitizeFileNamePart(parts.join(' - '), 180)}.${extension}`;
}

export function reportTypeSubtitle(checkType: string | null | undefined): string {
  const type = (checkType ?? '').toUpperCase();
  if (type === 'IDS') {
    return 'Überprüfung des BIM-Modells gegen die Information Delivery Specification (IDS)';
  }
  if (type === 'HYBRID') {
    return 'Kombinierte regelbasierte und KI-gestützte Überprüfung des BIM-Modells';
  }
  if (type === 'KI' || type === 'AI' || type === 'HOAI') {
    return 'KI-gestützte Überprüfung des BIM-Modells anhand konfigurierter Prüfschritte';
  }
  return 'Überprüfung des BIM-Modells';
}
